/**
 * @fileoverview État global de la vue (filtre, présentation, tri).
 * Source unique de vérité — toute la chaîne de rendu doit s'abonner ici plutôt
 * que de stocker des copies locales. Persistance automatique dans localStorage.
 */

const STORAGE_KEY = 'bd-collection.viewState';

/**
 * Valeurs par défaut. Sert aussi de liste des clés autorisées : toute autre clé
 * lue depuis localStorage est ignorée (protection contre un format ancien/altéré).
 * @type {Readonly<{ section: string, filter: string, layout: string, sort: string }>}
 */
const DEFAULT_STATE = Object.freeze({
    section: 'collection', // 'collection' | 'wishlist' — section active de l'application
    filter: 'all',         // 'all' | 'loaned' — filtre dans la section collection uniquement
    layout: 'grid',        // 'grid' | 'list'
    sort: 'recent',        // 'recent' | 'series' | 'title' | 'loanDate'
});

const VALID_KEYS = Object.keys(DEFAULT_STATE);

/** @type {{ filter: string, layout: string, sort: string }} */
let state = _loadFromStorage();

/** @type {Set<Function>} */
const subscribers = new Set();

/**
 * Lit l'état depuis localStorage en filtrant les clés inconnues.
 * @private
 * @returns {Object}
 */
function _loadFromStorage() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return { ...DEFAULT_STATE };

        const parsed = JSON.parse(raw);
        const clean = { ...DEFAULT_STATE };
        for (const k of VALID_KEYS) {
            if (k in parsed) clean[k] = parsed[k];
        }
        return clean;
    } catch {
        // Format invalide → reset silencieux aux valeurs par défaut
        return { ...DEFAULT_STATE };
    }
}

/**
 * Persiste l'état courant dans localStorage. Tolère un quota dépassé sans crasher.
 * @private
 */
function _persist() {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (err) {
        console.error('Persistance viewState impossible :', err);
    }
}

/**
 * Notifie tous les abonnés avec un instantané immutable de l'état.
 * @private
 */
function _notify() {
    const snapshot = { ...state };
    for (const cb of subscribers) {
        try {
            cb(snapshot);
        } catch (err) {
            console.error('Erreur dans un abonné viewState :', err);
        }
    }
}

/**
 * Retourne un instantané immutable de l'état courant.
 * @returns {{ filter: string, layout: string, sort: string }}
 */
export function get() {
    return { ...state };
}

/**
 * Met à jour un ou plusieurs champs et notifie les abonnés.
 * Aucune notification ni écriture si aucune valeur ne change réellement.
 * @param {Partial<{ filter: string, layout: string, sort: string }>} partial
 */
export function set(partial) {
    let changed = false;
    for (const k of VALID_KEYS) {
        if (k in partial && partial[k] !== state[k]) {
            state[k] = partial[k];
            changed = true;
        }
    }

    if (changed) {
        _persist();
        _notify();
    }
}

/**
 * Abonne une fonction aux changements d'état.
 * @param {(state: { filter: string, layout: string, sort: string }) => void} cb
 * @returns {() => void} Fonction de désabonnement
 */
export function subscribe(cb) {
    subscribers.add(cb);
    return () => subscribers.delete(cb);
}
